import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { ApiError } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { apiClient } from "@/lib/auth";
import { useAppStore } from "@/store";
import { workAssignmentKeys } from "./use-work-assignments";

type FormSchedule = components["schemas"]["FormSchedule"];
type FormScheduleList = components["schemas"]["FormScheduleList"];
type FormScheduleCreate = components["schemas"]["FormScheduleCreate"];
type FormScheduleUpdate = components["schemas"]["FormScheduleUpdate"];
type FormScheduleGenerateResponse = components["schemas"]["FormScheduleGenerateResponse"];

// Cache windows follow the field-credentials canon: short list staleness so
// fresh writes appear quickly, with keepPreviousData smoothing pagination.
const LIST_STALE_TIME = 60_000;
const LIST_GC_TIME = 5 * 60_000;
const DETAIL_STALE_TIME = 2 * 60_000;
const DETAIL_GC_TIME = 10 * 60_000;

/** Mirrors the listFormSchedules OpenAPI sort_by enum (the Go ORDER BY allow-list). */
export type FormScheduleSortBy = "name" | "start_date" | "created_at";

export interface FormSchedulesListParams {
  template_id?: string;
  active?: boolean;
  limit?: number;
  offset?: number;
  sort_by?: FormScheduleSortBy;
  sort_dir?: "asc" | "desc";
}

/**
 * Query-key factory for premise-inspection schedule caches. Org-param routes
 * thread orgId through EVERY key including detail — a detail key without
 * orgId would serve stale cross-org rows after an org switch.
 */
export const formScheduleKeys = {
  all: ["form-schedules"] as const,
  lists: () => [...formScheduleKeys.all, "list"] as const,
  list: (orgId: string, params: FormSchedulesListParams) =>
    [...formScheduleKeys.lists(), orgId, params] as const,
  detail: (orgId: string, id: string) => [...formScheduleKeys.all, "detail", orgId, id] as const,
} as const;

function withDefaults(params: FormSchedulesListParams): FormSchedulesListParams {
  return {
    ...params,
    limit: params.limit ?? 20,
    offset: params.offset ?? 0,
  };
}

/** Paginated RRULE-driven premise-inspection schedule list. */
export function useFormSchedules(params: FormSchedulesListParams = {}) {
  const orgId = useAppStore((s) => s.activeOrgId);
  const merged = withDefaults(params);
  return useQuery<FormScheduleList>({
    queryKey: formScheduleKeys.list(orgId ?? "", merged),
    queryFn: async () => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.GET("/orgs/{orgId}/form-schedules", {
        params: { path: { orgId }, query: merged },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to list form schedules";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    enabled: Boolean(orgId),
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    placeholderData: keepPreviousData,
  });
}

/** One form schedule (member facility set hydrated). */
export function useFormSchedule(id: string | null | undefined) {
  const orgId = useAppStore((s) => s.activeOrgId);
  return useQuery<FormSchedule>({
    queryKey: formScheduleKeys.detail(orgId ?? "", id ?? ""),
    queryFn: async () => {
      if (!orgId) throw new Error("No org context");
      if (!id) throw new Error("Schedule id is required");
      const { data, error, response } = await apiClient.GET(
        "/orgs/{orgId}/form-schedules/{scheduleId}",
        {
          params: { path: { orgId, scheduleId: id } },
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to load form schedule";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    enabled: Boolean(orgId) && Boolean(id),
    staleTime: DETAIL_STALE_TIME,
    gcTime: DETAIL_GC_TIME,
  });
}

/**
 * Create a schedule. The rrule is validated by the restricted RFC-5545
 * engine server-side — its 422 message is threaded through `ApiError` so
 * the schedule form renders it inline against the recurrence field.
 */
export function useCreateFormSchedule() {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async (body: FormScheduleCreate) => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.POST("/orgs/{orgId}/form-schedules", {
        params: { path: { orgId } },
        body,
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to create form schedule";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: formScheduleKeys.lists() });
    },
  });
}

/**
 * PATCH a schedule. `template_id` is immutable provenance; `facility_ids`
 * present = wholesale replace of the member set; `end_date` carries
 * three-state semantics (absent preserves, null clears, value sets).
 */
export function useUpdateFormSchedule(id: string) {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async (body: FormScheduleUpdate) => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.PATCH(
        "/orgs/{orgId}/form-schedules/{scheduleId}",
        {
          params: { path: { orgId, scheduleId: id } },
          body,
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to update form schedule";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: formScheduleKeys.detail(orgId, id) });
      }
      void queryClient.invalidateQueries({ queryKey: formScheduleKeys.lists() });
    },
  });
}

/**
 * Hard-delete a schedule. Generated assignments keep their schedule_id
 * provenance via RESTRICT — the 409 (schedule has generated assignments;
 * deactivate instead) is threaded through `ApiError.details`.
 */
export function useDeleteFormSchedule(id: string) {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.DELETE(
        "/orgs/{orgId}/form-schedules/{scheduleId}",
        {
          params: { path: { orgId, scheduleId: id } },
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to delete form schedule";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: formScheduleKeys.detail(orgId, id) });
      }
      void queryClient.invalidateQueries({ queryKey: formScheduleKeys.lists() });
    },
  });
}

/**
 * Materialize assignments for one schedule (org_admin; idempotent re-runs).
 * Returns the run report for the result toast. Cross-entity invalidation:
 * the generator inserts work_assignments rows, so the assignment list pages
 * must reflect them without a manual refresh. The schedule row itself does
 * not change (resume state is generator-internal), so schedule caches stay.
 */
export function useGenerateFormSchedule(id: string) {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async (): Promise<FormScheduleGenerateResponse> => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.POST(
        "/orgs/{orgId}/form-schedules/{scheduleId}/generate",
        {
          params: { path: { orgId, scheduleId: id } },
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to generate assignments";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workAssignmentKeys.lists() });
    },
  });
}

export { LIST_STALE_TIME, LIST_GC_TIME, DETAIL_STALE_TIME, DETAIL_GC_TIME };

export type {
  FormSchedule,
  FormScheduleList,
  FormScheduleCreate,
  FormScheduleUpdate,
  FormScheduleGenerateResponse,
};
